import React, { useMemo } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { IconButton } from "@mui/material";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { AsyncContent } from "components";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HC_more from "highcharts/highcharts-more";
import accessibility from "highcharts/modules/accessibility";
import { useActionHook, useDidUpdate } from "hooks";
import { useReportsActions } from "store";
import { GetPieChartData } from "store/types";

import "./DealsChart.styles.scss";

accessibility(Highcharts);

HC_more(Highcharts);
interface DealsChartProps {
  queries: GetPieChartData | null;
  onClose: () => void;
}

const defaultOptions: Highcharts.Options = {
  chart: {
    type: "packedbubble",
    height: "100%",
    animation: false
  },
  exporting: {
    enabled: false
  },
  credits: {
    enabled: false
  },
  title: {
    text: "Carbon emissions around the world (2014)"
  },
  tooltip: {
    useHTML: true,
    pointFormat: "<b>{point.name}:</b> {point.value}"
  },
  plotOptions: {
    packedbubble: {
      minSize: "20%",
      maxSize: "100%",
      // zMin: 0,
      // zMax: 1000,
      layoutAlgorithm: {
        gravitationalConstant: 0.05,
        // splitSeries: true,
        // splitSeries: "name",
        enableSimulation: false,
        seriesInteraction: false,
        dragBetweenSeries: true,
        parentNodeLimit: true
      },
      dataLabels: {
        enabled: true,
        format: "{point.name}",
        filter: {
          property: "y",
          operator: ">",
          value: 250
        },
        style: {
          color: "black",
          textOutline: "none",
          fontWeight: "normal"
        }
      }
    }
  }
};

export type DemoDataItem = {
  name: string;
  y: number;
  deals: { [key: string]: number };
};

type DemoChartData = {
  title: string;
  data: {
    colorByPoint: boolean;
    innerSize: string;
    currency: string;
    name: string;
    type: string;
    data: DemoDataItem[];
  };
};

const transfromData = (items: DemoDataItem): Highcharts.SeriesPackedbubbleOptions => {
  return {
    name: items.name,
    type: "packedbubble",
    data: Object.keys(items.deals).map((key) => ({
      name: key,
      value: Number(items.deals[key].toFixed(2))
    }))
  };
};

export const DealsChart: React.FC<DealsChartProps> = ({ queries, onClose }) => {
  const { getPieChartAction } = useReportsActions();
  const { data, loading, action, resetData } = useActionHook(getPieChartAction);

  useDidUpdate(() => {
    if (queries) {
      action(queries);
    } else {
      resetData();
    }
  }, [queries]);

  const pieOptions: Highcharts.Options = useMemo(() => {
    const options: Highcharts.Options = {
      ...defaultOptions,
      title: { ...defaultOptions.title, text: data?.title }
    };

    if (data) {
      options.series = (data as unknown as DemoChartData).data.data.map(transfromData);
    }

    return options;
  }, [data]);

  return (
    <Modal open={!!queries} onClose={onClose}>
      <Box sx={style}>
        <div className="deals-chart">
          <div className="deals-chart__head">
            <span>Deals Chart</span>
            <IconButton onClick={onClose}>
              <CloseIcon className="icon" />
            </IconButton>
          </div>
          <AsyncContent
            spinnerProps={{ size: 150 }}
            loaderClassName="deals-chart__body--loading"
            className="deals-chart__body"
            loading={loading}
          >
            <HighchartsReact highcharts={Highcharts} constructorType={"chart"} options={pieOptions} />
          </AsyncContent>
        </div>
      </Box>
    </Modal>
  );
};

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  outline: "none"
};
